// theme.js — single source of truth for Mono's design tokens.
//
// Replaces the six duplicated `P` / `FN` / `MO` / `TC` objects that previously
// lived inline in App.js, SelectionCanvas.js, PlannerPanel.jsx, etc. Everything
// now flows from here through a React Context (D2 = Context approach).
//
// The palette is the warm "editorial / print-shop" language from the Mono
// design (cream paper, near-black ink, terracotta accent, Geist + Instrument
// Serif), and supports a light and a dark variant.
//
// COMPATIBILITY NOTE: `makePalette` returns the new editorial token names
// (bg / sheet / surface / elev / ink / muted / dim / rule / ruleSoft /
// accent / ok / warn / danger / …) AND a set of legacy aliases for the short
// names the existing components already use (sf, sa, bd, tx, td, tm, ac, as,
// os, wn, er, es). That lets the restyle change token *values* without
// rewriting thousands of `P.xx` references. New code written in later phases
// should prefer the native editorial names; the aliases are a migration bridge.

import React from 'react';

// ─── Accents ────────────────────────────────────────────────────────────────
// Terracotta is the locked default (D5). The others are kept so a future accent
// switcher is a one-line change; we deliberately don't expose a UI for them now.
export const ACCENTS = {
  terracotta: { name: 'Terracotta', hex: '#BE5A3C', soft: 'rgba(190,90,60,.10)', line: 'rgba(190,90,60,.32)' },
  ochre:      { name: 'Ochre',      hex: '#B98318', soft: 'rgba(185,131,24,.10)', line: 'rgba(185,131,24,.32)' },
  kraft:      { name: 'Kraft',      hex: '#7B5A33', soft: 'rgba(123,90,51,.10)', line: 'rgba(123,90,51,.32)' },
  ink:        { name: 'Ink',        hex: '#1A1814', soft: 'rgba(26,24,20,.06)',  line: 'rgba(26,24,20,.32)' },
};

// ─── Type pairings ───────────────────────────────────────────────────────────
// "editorial" is the locked default. display = Instrument Serif (page titles),
// sans = Geist (UI), mono = Geist Mono (numbers / technical).
export const TYPE_PAIRS = {
  editorial: {
    name: 'Editorial',
    display: '"Instrument Serif", "Times New Roman", serif',
    sans: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
    mono: '"Geist Mono", "JetBrains Mono", ui-monospace, monospace',
    displayWeight: 400, displayItalic: false, h1Size: 38, h1Track: -0.01,
  },
  technical: {
    name: 'Technical',
    display: '"Geist Mono", "JetBrains Mono", ui-monospace, monospace',
    sans: '"Geist", ui-sans-serif, system-ui, sans-serif',
    mono: '"Geist Mono", "JetBrains Mono", ui-monospace, monospace',
    displayWeight: 500, displayItalic: false, h1Size: 22, h1Track: 0.04, h1Upper: true,
  },
  quiet: {
    name: 'Quiet',
    display: '"Geist", ui-sans-serif, system-ui, sans-serif',
    sans: '"Geist", ui-sans-serif, system-ui, sans-serif',
    mono: '"Geist Mono", "JetBrains Mono", ui-monospace, monospace',
    displayWeight: 400, h1Size: 28, h1Track: -0.015,
  },
};

// ─── Density ─────────────────────────────────────────────────────────────────
// Used by the two-pane workspaces in later phases (rail width, padding, gaps).
// "balanced" is the locked default.
export const DENSITIES = {
  cozy:     { gap: 20, rail: 304, pad: 28, cardPad: 16 },
  balanced: { gap: 14, rail: 288, pad: 22, cardPad: 13 },
  dense:    { gap: 10, rail: 264, pad: 16, cardPad: 11 },
};

// Carton type colours (sheet nest fills / outlines). Tuned to read on cream and
// on warm-dark alike — earthy and distinguishable rather than neon.
export const TC = ['#BE5A3C', '#2A6F86', '#3F7F4C', '#B98318', '#8E4585', '#3A6EA5'];

// ─── Palette generator ───────────────────────────────────────────────────────
export function makePalette(t) {
  const A = ACCENTS[t && t.accent] || ACCENTS.terracotta;
  const base = (!t || !t.dark)
    ? {
        bg: '#F2EBDB', sheet: '#FDFAF0', surface: '#FAF4E6', elev: '#FFFCF3',
        ink: '#1A1814', muted: '#6E665C', dim: '#A09787',
        rule: '#E5DCC4', ruleSoft: '#EFE7D0',
        accent: A.hex, accentSoft: A.soft, accentLine: A.line,
        chipBg: '#1A1814', chipInk: '#FDFAF0',
        ok: '#3F7F4C', okSoft: 'rgba(63,127,76,.12)', okLine: 'rgba(63,127,76,.34)',
        warn: '#B5791A', warnSoft: 'rgba(181,121,26,.12)', warnLine: 'rgba(181,121,26,.34)',
        danger: '#B5462E', dangerSoft: 'rgba(181,70,46,.10)', dangerLine: 'rgba(181,70,46,.34)',
        isDark: false,
      }
    : {
        bg: '#1A1612', sheet: '#221C15', surface: '#1F1A14', elev: '#241E16',
        ink: '#F0E8D6', muted: '#A29985', dim: '#6F6657',
        rule: '#33291F', ruleSoft: '#291F18',
        accent: A.hex, accentSoft: A.soft, accentLine: A.line,
        chipBg: '#F0E8D6', chipInk: '#1A1612',
        ok: '#69B07A', okSoft: 'rgba(105,176,122,.14)', okLine: 'rgba(105,176,122,.34)',
        warn: '#D2A24A', warnSoft: 'rgba(210,162,74,.14)', warnLine: 'rgba(210,162,74,.34)',
        danger: '#D9745A', dangerSoft: 'rgba(217,116,90,.14)', dangerLine: 'rgba(217,116,90,.34)',
        isDark: true,
      };

  // Legacy aliases → editorial values (migration bridge; see header note).
  return {
    ...base,
    sf: base.surface, sa: base.elev, bd: base.rule,
    tx: base.ink, td: base.muted, tm: base.dim,
    ac: base.accent, as: base.accentSoft,
    os: base.okSoft, wn: base.warn, ws: base.warnSoft, er: base.danger, es: base.dangerSoft,
    // SelectionCanvas highlight hues (tool-specific accents).
    bl: '#3A6EA5', cyan: '#1E8A9E',
  };
}

// ─── Shared style helpers that depend on the palette ─────────────────────────
// Exposed via the context so existing `style={inp}` / `style={kbdStyle}` call
// sites keep working after a one-line destructure.
function makeInp(P, MO) {
  return {
    display: 'block', width: '100%', marginTop: 4, padding: '7px 10px',
    borderRadius: 6, border: `0.5px solid ${P.rule}`, background: P.elev,
    color: P.ink, fontFamily: MO, fontSize: 13, boxSizing: 'border-box', outline: 'none',
  };
}
function makeKbd(P, MO) {
  return {
    fontFamily: MO, fontSize: 10, padding: '1px 5px',
    background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 3, color: P.muted,
  };
}

// ─── Context / provider / hook ───────────────────────────────────────────────
const STORE_KEY = 'mono.theme';
const DEFAULTS = { accent: 'terracotta', typePairing: 'editorial', density: 'balanced', dark: false, unit: 'mm' };

// Length units. Canonical storage everywhere (state + backend payloads) is mm;
// the unit toggle is a display/input convenience only — nothing sent to the
// backend is ever converted. 1 in = 25.4 mm.
export const MM_PER_IN = 25.4;
const roundTo = (n, dp) => { const f = 10 ** dp; return Math.round((Number(n) || 0) * f) / f; };
/** mm → the active display unit, rounded for an input field (Number). */
function mmToDisplay(mm, unit) {
  return unit === 'in' ? roundTo(mm / MM_PER_IN, 3) : roundTo(mm, 2);
}
/** A value typed in the active display unit → canonical mm (Number). */
function displayToMm(val, unit) {
  const n = Number(val) || 0;
  return unit === 'in' ? n * MM_PER_IN : n;
}
/** mm → a readout string with the active unit, e.g. "500 mm" or "19.69 in". */
function fmtLen(mm, unit) {
  return unit === 'in' ? `${roundTo(mm / MM_PER_IN, 2)} in` : `${roundTo(mm, 1)} mm`;
}

function loadTweaks() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {}
  return { ...DEFAULTS };
}

const ThemeContext = React.createContext(null);

export function ThemeProvider({ children }) {
  const [t, setT] = React.useState(loadTweaks);

  React.useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(t)); } catch (e) {}
  }, [t]);

  const value = React.useMemo(() => {
    const P = makePalette(t);
    const TP = TYPE_PAIRS[t.typePairing] || TYPE_PAIRS.editorial;
    const D = DENSITIES[t.density] || DENSITIES.balanced;
    const FN = TP.sans;          // legacy alias for the old `FN` (UI sans)
    const MO = TP.mono;          // legacy alias for the old `MO` (mono)
    const DISPLAY = TP.display;  // serif display for page titles
    const unit = t.unit === 'in' ? 'in' : 'mm';
    return {
      t, setTweak: (k, v) => setT((p) => ({ ...p, [k]: v })),
      toggleDark: () => setT((p) => ({ ...p, dark: !p.dark })),
      // Units (canonical mm; these only affect display/input).
      unit,
      setUnit: (u) => setT((p) => ({ ...p, unit: u === 'in' ? 'in' : 'mm' })),
      toggleUnit: () => setT((p) => ({ ...p, unit: p.unit === 'in' ? 'mm' : 'in' })),
      toDisplay: (mm) => mmToDisplay(mm, unit),
      fromDisplay: (val) => displayToMm(val, unit),
      fmtLen: (mm) => fmtLen(mm, unit),
      P, TP, D, FN, MO, DISPLAY, TC,
      inp: makeInp(P, MO), kbdStyle: makeKbd(P, MO),
    };
  }, [t]);

  // Keep the area outside #root (and form controls / scrollbars) in sync with
  // the active palette, including dark mode.
  React.useEffect(() => {
    document.body.style.background = value.P.bg;
    document.documentElement.style.colorScheme = value.P.isDark ? 'dark' : 'light';
  }, [value.P.bg, value.P.isDark]);

  const css = `
    ::selection { background: ${value.P.accentSoft}; }
    ::-webkit-scrollbar { width: 8px; height: 8px; }
    ::-webkit-scrollbar-track { background: ${value.P.surface}; }
    ::-webkit-scrollbar-thumb { background: ${value.P.rule}; border-radius: 4px; }
    @keyframes mono-spin { to { transform: rotate(360deg); } }
    @keyframes mono-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes mono-pulse { 0%,100% { opacity: 1; } 50% { opacity: .5; } }
    @media (max-width: 880px) {
      .mono-twopane { grid-template-columns: 1fr !important; }
      /* One column: the rail becomes a BAND ABOVE the sheet rather than a
         column beside it, so pinning it would park its own controls on top of
         the work surface. Inline styles lose to !important, which is why the
         pin lives in the component and its undo lives here. */
      .mono-rail { position: static !important; max-height: none !important; overflow: visible !important; }
    }
  `;

  return (
    <ThemeContext.Provider value={value}>
      <style>{css}</style>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
